// Read-only CW staff notes for a quote, shown inside its Customer 360 timeline entry.
// Latest two inline; "View all" opens a solid pop-up. Notes are added on the quote itself.
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { StickyNote, X, ExternalLink, Star } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const when = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d) ? "" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

function NoteLine({ n, clamp }) {
  return (
    <div className="flex gap-2 py-1.5" data-testid={`quote-note-${n.id}`}>
      {n.system ? <Star className="w-3.5 h-3.5 mt-0.5 shrink-0 text-violet-300" /> : <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />}
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-mono text-slate-500">{n.system ? "System" : n.by || "Staff"} · {when(n.ts)}</div>
        <div className={`text-[12.5px] text-slate-200 whitespace-pre-wrap break-words ${clamp ? "line-clamp-2" : ""}`}>{String(n.body).replace(/^★\s*/, "")}</div>
      </div>
    </div>
  );
}

export default function QuoteNotesPeek({ quoteId, quoteRef, notes = [] }) {
  const [all, setAll] = useState(false);
  const lightOn = useTheme().theme === "light";
  if (!notes.length) return <div className="text-[11px] text-slate-500 font-mono" data-testid={`quote-notes-empty-${quoteId}`}>No CW staff notes on this quote yet.</div>;
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5" data-testid={`quote-notes-peek-${quoteId}`}>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
        <span>CW staff notes · {notes.length}</span>
        {notes.length > 2 && (
          <button type="button" onClick={() => setAll(true)} className="normal-case tracking-normal text-[11px] text-orange-300 hover:text-orange-200" data-testid={`quote-notes-view-all-${quoteId}`}>View all {notes.length}</button>
        )}
      </div>
      <div className="divide-y divide-white/[0.04]">{notes.slice(0, 2).map((n) => <NoteLine key={n.id} n={n} clamp />)}</div>
      {all && createPortal(
        <div className={`fixed inset-0 z-[220] flex items-center justify-center p-3 ${lightOn ? "bg-slate-900/45" : "bg-black/70"}`} onClick={() => setAll(false)} data-talmia-theme={lightOn ? "light" : undefined}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: lightOn ? "#FFFFFF" : "#0F1420" }}
            className="w-full max-w-[560px] max-h-[85vh] flex flex-col rounded-2xl border border-white/10 shadow-2xl" data-testid={`quote-notes-modal-${quoteId}`}>
            <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-white">CW staff notes · {quoteRef}</div>
              <div className="flex items-center gap-1">
                <Link to={`/quotes/${quoteId}/edit`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] text-orange-300 hover:bg-white/5" data-testid={`quote-notes-open-quote-${quoteId}`}><ExternalLink className="w-3.5 h-3.5" /> Open quote</Link>
                <button type="button" onClick={() => setAll(false)} className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5" data-testid={`quote-notes-close-${quoteId}`} aria-label="Close"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="px-4 py-2 overflow-y-auto divide-y divide-white/[0.05]">{notes.map((n) => <NoteLine key={n.id} n={n} />)}</div>
          </div>
        </div>, document.body)}
    </div>
  );
}
